(function () {
  'use strict';

  const MAX_PHOTO_BYTES = 20 * 1024 * 1024;
  const HANDLE_RADIUS = 24;
  const MESH_SIZE = 8;
  const DEFAULT_OCCLUSION_BRUSH_RADIUS = 28;
  const MIN_OCCLUSION_BRUSH_RADIUS = 12;
  const MAX_OCCLUSION_BRUSH_RADIUS = 72;
  const ROOM_ANALYSIS_MAX_EDGE = 960;
  const FLOOR_FORESHORTENING = 0.58;
  let roomSession = null;
  let roomWorker = null;
  let roomRequestSequence = 0;
  const roomRequests = new Map();
  const catalogRequests = new Map();

  async function catalogProduct(url, handle) {
    if (!url || !handle) return null;
    if (!catalogRequests.has(url)) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      catalogRequests.set(url, fetch(url, { credentials: 'omit', signal: controller.signal }).then((response) => {
        if (!response.ok) throw new Error('catalog-unavailable');
        return response.json();
      }).catch((error) => { catalogRequests.delete(url); throw error; })
        .finally(() => clearTimeout(timeout)));
    }
    const catalog = await catalogRequests.get(url);
    return catalog.products.find((product) => product.handle === handle);
  }

  function stopRoomWorker(error) {
    roomWorker?.terminate();
    roomWorker = null;
    for (const request of roomRequests.values()) {
      clearTimeout(request.timeout);
      request.reject(error);
    }
    roomRequests.clear();
  }

  function getRoomWorker(url) {
    if (!roomWorker) {
      if (!url || !window.Worker) throw new Error('room-worker-unavailable');
      const bootstrap = URL.createObjectURL(new Blob([
        'importScripts(' + JSON.stringify(new URL(url, window.location.href).href) + ');'
      ], {type: 'application/javascript'}));
      roomWorker = new Worker(bootstrap);
      URL.revokeObjectURL(bootstrap);
      roomWorker.onmessage = ({data}) => {
        const request = roomRequests.get(data.id);
        if (!request) return;
        if ('progress' in data || data.phase) { request.progress?.(data); return; }
        clearTimeout(request.timeout);
        roomRequests.delete(data.id);
        if (data.error) request.reject(new Error(data.error));
        else request.resolve(data);
      };
      roomWorker.onerror = () => stopRoomWorker(new Error('room-worker-failed'));
    }
    return roomWorker;
  }

  function analyzeRoom(url, imageData, progress) {
    return new Promise((resolve, reject) => {
      const worker = getRoomWorker(url);
      // A room photo is shared across editors. Only its latest analysis matters.
      for (const request of roomRequests.values()) {
        clearTimeout(request.timeout);
        request.reject(new Error('room-analysis-replaced'));
      }
      roomRequests.clear();
      const id = ++roomRequestSequence;
      const timeout = window.setTimeout(() => stopRoomWorker(new Error('room-analysis-timeout')), 120000);
      roomRequests.set(id, {resolve, reject, timeout, progress});
      worker.postMessage({id, width: imageData.width, height: imageData.height, pixels: imageData.data.buffer},
        [imageData.data.buffer]);
    });
  }

  function smoothstep(minimum, maximum, value) {
    const normalized = Math.max(0, Math.min(1, (value - minimum) / (maximum - minimum)));
    return normalized * normalized * (3 - 2 * normalized);
  }

  function distance(a, b) {
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  function centerOf(points) {
    return points.reduce(
      function (center, point) {
        center.x += point.x / points.length;
        center.y += point.y / points.length;
        return center;
      },
      { x: 0, y: 0 },
    );
  }

  function pointInPolygon(point, polygon) {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const a = polygon[i];
      const b = polygon[j];
      const intersects =
        a.y > point.y !== b.y > point.y &&
        point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y || 0.0001) + a.x;
      if (intersects) inside = !inside;
    }
    return inside;
  }

  function validQuad(points) {
    let sign = 0;
    for (let i = 0; i < 4; i += 1) {
      const a = points[i], b = points[(i + 1) % 4], c = points[(i + 2) % 4];
      const cross = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
      if (Math.abs(cross) < 12 || distance(a, b) < 18) return false;
      if (sign && Math.sign(cross) !== sign) return false;
      sign = Math.sign(cross);
    }
    return true;
  }

  function solveLinearSystem(matrix, values) {
    const size = values.length;
    const rows = matrix.map(function (row, index) {
      return row.concat(values[index]);
    });
    for (let column = 0; column < size; column += 1) {
      let pivot = column;
      for (let row = column + 1; row < size; row += 1) {
        if (Math.abs(rows[row][column]) > Math.abs(rows[pivot][column])) pivot = row;
      }
      if (Math.abs(rows[pivot][column]) < 1e-9) return null;
      [rows[column], rows[pivot]] = [rows[pivot], rows[column]];
      const divisor = rows[column][column];
      for (let item = column; item <= size; item += 1) rows[column][item] /= divisor;
      for (let row = 0; row < size; row += 1) {
        if (row === column) continue;
        const factor = rows[row][column];
        for (let item = column; item <= size; item += 1) {
          rows[row][item] -= factor * rows[column][item];
        }
      }
    }
    return rows.map(function (row) {
      return row[size];
    });
  }

  function homographyFor(corners) {
    const source = [
      { u: 0, v: 0 },
      { u: 1, v: 0 },
      { u: 1, v: 1 },
      { u: 0, v: 1 },
    ];
    const matrix = [];
    const values = [];
    source.forEach(function (sourcePoint, index) {
      const destination = corners[index];
      const u = sourcePoint.u;
      const v = sourcePoint.v;
      matrix.push([u, v, 1, 0, 0, 0, -u * destination.x, -v * destination.x]);
      values.push(destination.x);
      matrix.push([0, 0, 0, u, v, 1, -u * destination.y, -v * destination.y]);
      values.push(destination.y);
    });
    return solveLinearSystem(matrix, values);
  }

  function projectPoint(homography, u, v) {
    const denominator = homography[6] * u + homography[7] * v + 1;
    return {
      x: (homography[0] * u + homography[1] * v + homography[2]) / denominator,
      y: (homography[3] * u + homography[4] * v + homography[5]) / denominator,
    };
  }

  function drawTriangle(context, image, source, destination) {
    const x0 = source[0].x;
    const y0 = source[0].y;
    const x1 = source[1].x;
    const y1 = source[1].y;
    const x2 = source[2].x;
    const y2 = source[2].y;
    const denominator = x0 * (y1 - y2) + x1 * (y2 - y0) + x2 * (y0 - y1);
    if (Math.abs(denominator) < 0.0001) return;
    const a =
      (destination[0].x * (y1 - y2) +
        destination[1].x * (y2 - y0) +
        destination[2].x * (y0 - y1)) /
      denominator;
    const b =
      (destination[0].y * (y1 - y2) +
        destination[1].y * (y2 - y0) +
        destination[2].y * (y0 - y1)) /
      denominator;
    const c =
      (destination[0].x * (x2 - x1) +
        destination[1].x * (x0 - x2) +
        destination[2].x * (x1 - x0)) /
      denominator;
    const d =
      (destination[0].y * (x2 - x1) +
        destination[1].y * (x0 - x2) +
        destination[2].y * (x1 - x0)) /
      denominator;
    const e =
      (destination[0].x * (x1 * y2 - x2 * y1) +
        destination[1].x * (x2 * y0 - x0 * y2) +
        destination[2].x * (x0 * y1 - x1 * y0)) /
      denominator;
    const f =
      (destination[0].y * (x1 * y2 - x2 * y1) +
        destination[1].y * (x2 * y0 - x0 * y2) +
        destination[2].y * (x0 * y1 - x1 * y0)) /
      denominator;

    context.save();
    context.beginPath();
    const centroid = centerOf(destination);
    const expanded = destination.map((point) => {
      const length = Math.max(1, distance(point, centroid));
      return { x: point.x + (point.x - centroid.x) * 0.45 / length,
        y: point.y + (point.y - centroid.y) * 0.45 / length };
    });
    context.moveTo(expanded[0].x, expanded[0].y);
    context.lineTo(expanded[1].x, expanded[1].y);
    context.lineTo(expanded[2].x, expanded[2].y);
    context.closePath();
    context.clip();
    context.transform(a, b, c, d, e, f);
    context.drawImage(image, 0, 0);
    context.restore();
  }

  class RugVisualizer {
    constructor(root) {
      this.root = root;
      this.dialog = root.querySelector('[data-visualizer-dialog]');
      this.stage = root.querySelector('[data-visualizer-stage]');
      this.canvas = root.querySelector('[data-visualizer-canvas]');
      this.context = this.canvas.getContext('2d', { alpha: false });
      this.empty = root.querySelector('[data-visualizer-empty]');
      this.status = root.querySelector('[data-visualizer-status]');
      this.sizeSelect = root.querySelector('[data-visualizer-size]');
      this.occlusionToggle = root.querySelector('[data-visualizer-occlusion-toggle]');
      this.occlusionTools = root.querySelector('[data-visualizer-occlusion-tools]');
      this.brushSizeControl = root.querySelector('[data-visualizer-occlusion-brush]');
      this.brushSizeOutput = root.querySelector('[data-visualizer-occlusion-brush-value]');
      this.shape = ['round', 'oval', 'freeform'].includes(root.dataset.shape) ? root.dataset.shape : 'rectangle';
      this.sectionId = root.dataset.sectionId;
      this.selectedVariantId = String(root.dataset.selectedVariantId || '');
      this.data = JSON.parse(root.querySelector('[data-visualizer-data]').textContent);
      this.variants = this.data.variants.filter(function (variant) {
        return variant.available;
      });
      this.photo = null;
      this.rugImage = null;
      this.rugTexture = null;
      this.corners = null;
      this.viewport = { width: 0, height: 0 };
      this.pointers = new Map();
      this.drag = null;
      this.gesture = null;
      this.frameRequested = false;
      this.previousOverflow = '';
      this.lastFocus = null;
      this.photoZoom = 1;
      this.rugOpacity = 1;
      this.rugBrightness = 1;
      this.shadowStrength = 0.65;
      this.occlusionMode = false;
      this.occlusionAction = 'hide';
      this.brushRadius = DEFAULT_OCCLUSION_BRUSH_RADIUS;
      this.occlusionStrokes = [];
      this.brushPoint = null;
      this.automaticOcclusionMask = null;
      this.automaticOcclusionEnabled = true;
      this.floorBoundary = null;
      this.occlusionDirty = true;
      this.analysisSequence = 0;
      this.occlusionMaskCanvas = document.createElement('canvas');
      this.occlusionForegroundCanvas = document.createElement('canvas');
      this.rugShadowCanvas = document.createElement('canvas');
      this.history = [];
      this.historyIndex = -1;
      this.interactionChanged = false;
      this.productSequence = 0;
      this.rugLoadSequence = 0;
      this.variantAssets = null;
      this.rugUrl = null;
      this.placementDimensions = null;
      this.bind();
    }

    bind() {
      this.root.querySelector('[data-visualizer-open]').addEventListener('click', () => this.open());
      this.root.querySelector('[data-visualizer-close]').addEventListener('click', () => this.close());
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.root.querySelectorAll('[data-visualizer-file], [data-visualizer-camera]').forEach((input) => {
        input.addEventListener('change', (event) => {
          const file = event.target.files[0];
          event.target.value = '';
          this.readPhoto(file);
        });
      });
      this.sizeSelect.addEventListener('change', () => {
        this.updateRatioForVariant(this.sizeSelect.value, true);
        this.loadRug();
        this.emit('size_selected');
      });
      this.root.querySelector('[data-visualizer-reset]').addEventListener('click', () => this.reset(true));
      this.root.querySelectorAll('[data-visualizer-rotate]').forEach((button) => {
        button.addEventListener('click', () => this.rotate(Number(button.dataset.visualizerRotate), true));
      });
      this.root.querySelector('[data-visualizer-undo]').addEventListener('click', () => this.undo());
      this.root.querySelector('[data-visualizer-redo]').addEventListener('click', () => this.redo());
      this.occlusionToggle.addEventListener('click', () => this.setOcclusionMode(true));
      this.root.querySelector('[data-visualizer-occlusion-done]').addEventListener('click', () =>
        this.setOcclusionMode(false),
      );
      this.root.querySelectorAll('[data-visualizer-occlusion-action]').forEach((button) => {
        button.addEventListener('click', () => this.setOcclusionAction(button.dataset.visualizerOcclusionAction));
      });
      this.brushSizeControl.addEventListener('input', () => {
        this.brushRadius = Math.min(
          MAX_OCCLUSION_BRUSH_RADIUS,
          Math.max(MIN_OCCLUSION_BRUSH_RADIUS, Number(this.brushSizeControl.value)),
        );
        this.brushSizeOutput.textContent = `${this.brushRadius} px`;
        this.requestRender();
      });
      this.root.querySelectorAll('[data-visualizer-control]').forEach((control) => {
        const property = control.dataset.visualizerControl;
        control.addEventListener('input', () => {
          this[property] = Number(control.value) / 100;
          if (property === 'photoZoom') this.occlusionDirty = true;
          this.requestRender();
        });
        control.addEventListener('change', () => this.commitHistory());
      });
      this.root.querySelector('[data-visualizer-confirm]').addEventListener('click', () => this.confirm());
      this.root.addEventListener('heikoa:select-product', (event) => this.setProduct(event.detail));
      this.root.querySelector('[data-visualizer-clear-photo]')?.addEventListener('click', () => this.clearPhoto());
      this.root.querySelector('[data-visualizer-comparison]')?.addEventListener('change', (event) => {
        this.stage.classList.toggle('is-original', event.target.checked);
        this.requestRender();
      });
      this.canvas.addEventListener('pointerdown', (event) => this.pointerDown(event));
      this.root.querySelector('[data-visualizer-auto-occlusion]')?.addEventListener('change', (event) => {
        this.automaticOcclusionEnabled = event.target.checked;
        this.occlusionDirty = true;
        this.requestRender();
      });
      this.canvas.addEventListener('keydown', (event) => {
        if (!this.photo || !this.corners || this.occlusionMode) return;
        const directions = {ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1]};
        const direction = directions[event.key];
        if (!direction) return;
        event.preventDefault();
        const step = event.shiftKey ? 24 : 8;
        this.corners = this.corners.map(point => ({x: point.x + direction[0] * step, y: point.y + direction[1] * step}));
        this.commitHistory();
        this.requestRender();
      });
      this.canvas.addEventListener('pointermove', (event) => this.pointerMove(event));
      this.canvas.addEventListener('pointerup', (event) => this.pointerUp(event));
      this.canvas.addEventListener('pointercancel', (event) => this.pointerUp(event));
      this.canvas.addEventListener('pointerleave', () => {
        if (!this.pointers.size) {
          this.brushPoint = null;
          this.requestRender();
        }
      });
      this.canvas.addEventListener(
        'wheel',
        (event) => {
          if (!this.photo || !this.corners || this.occlusionMode) return;
          event.preventDefault();
          this.scale(event.deltaY < 0 ? 1.05 : 0.95, true);
        },
        { passive: false },
      );
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.dialog.hidden) this.close();
      });
      this.dialog.addEventListener('keydown', (event) => this.trapFocus(event));
      window.addEventListener('variantMediaUpdate', (event) => {
        const variant = event.detail && event.detail.variant;
        if (variant && variant.id) this.selectVariant(String(variant.id), false);
      });
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(this.stage);
      this.syncControlValues();
      this.updateHistoryButtons();
    }

    open() {
      if (!this.dialog.hidden && this.dialog.open) {
        this.loadRug();
        this.requestRender();
        return;
      }
      this.lastFocus = document.activeElement;
      this.dialog.hidden = false;
      if (this.dialog.showModal && !this.dialog.open) this.dialog.showModal();
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.root.querySelector('[data-visualizer-close]').focus();
      this.emit('opened');
      requestAnimationFrame(() => {
        this.resize();
        if (roomSession && this.photo !== roomSession.photo) {
          this.photo = roomSession.photo;
          this.automaticOcclusionMask = roomSession.mask;
          this.floorBoundary = roomSession.floorBoundary;
          this.occlusionStrokes = roomSession.strokes || [];
          this.occlusionToggle.disabled = false;
          this.empty.hidden = true;
          this.occlusionDirty = true;
          this.reset(false);
          this.occlusionStrokes = roomSession.strokes || [];
          this.history = [];
          this.historyIndex = -1;
          this.commitHistory();
          if (roomSession.analysis) this.adoptRoomAnalysis(roomSession);
        }
        this.loadRug();
      });
    }

    close() {
      if (roomSession && roomSession.photo === this.photo) {
        roomSession.strokes = this.occlusionStrokes;
      }
      this.setOcclusionMode(false);
      if (this.dialog.open) this.dialog.close();
      this.dialog.hidden = true;
      document.documentElement.style.overflow = this.previousOverflow;
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    }

    trapFocus(event) {
      if (event.key !== 'Tab' || this.dialog.hidden) return;
      const focusable = Array.from(
        this.dialog.querySelectorAll(
          'button:not([disabled]), select:not([disabled]), input:not([disabled]), summary, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => element.getClientRects().length > 0);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    async loadRug() {
      const loadSequence = ++this.rugLoadSequence;
      const sequence = this.productSequence;
      let variantId = String(this.sizeSelect.value);
      this.rugLoading = true;
      this.setLoading(true);
      try {
        if (!this.variantAssets && this.root.dataset.catalogUrl) {
          let product;
          try {
            product = await catalogProduct(this.root.dataset.catalogUrl, this.root.dataset.productHandle);
          } catch (error) {
            // The initial texture is embedded in Liquid and remains usable offline.
            // A different design requires its catalogue mapping to avoid showing a wrong rug.
            if (variantId !== String(this.root.dataset.selectedVariantId)) throw error;
          }
          if (loadSequence !== this.rugLoadSequence || sequence !== this.productSequence) return;
          if (product) this.variantAssets = product.variantAssets || {};
          if (product?.excludedVariants?.length) {
            const excluded = new Set(product.excludedVariants.map(String));
            this.variants = this.variants.filter((variant) => !excluded.has(String(variant.id)));
            Array.from(this.sizeSelect.options).forEach((option) => { if (excluded.has(option.value)) option.remove(); });
            variantId = String(this.sizeSelect.value);
          }
        }
        const asset = this.variantAssets?.[variantId];
        const url = asset?.imageUrl || (asset?.asset
          ? new URL(asset.asset, new URL(this.root.dataset.catalogUrl || window.location.href, window.location.href)).href
          : this.root.dataset.imageUrl);
        if (url === this.rugUrl && this.rugImage) return;
        this.rugImage = null;
        this.rugTexture = null;
        this.requestRender();
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.decoding = 'async';
        image.src = url;
        await image.decode();
        if (sequence !== this.productSequence || loadSequence !== this.rugLoadSequence) return;
        if (asset?.shape) this.shape = asset.shape;
        this.rugUrl = url;
        this.rugImage = image;
        this.rugTexture = this.prepareRugTexture(image);
        if (!this.corners) this.reset(false);
        else if (!this.placementDimensions) this.updateRatioForVariant(variantId, false);
        this.requestRender();
      } catch (error) {
        if (sequence === this.productSequence && loadSequence === this.rugLoadSequence) {
          this.rugImage = null;
          this.rugTexture = null;
          this.rugUrl = null;
          this.requestRender();
          this.status.textContent = this.root.dataset.rugError;
        }
      } finally {
        if (sequence === this.productSequence && loadSequence === this.rugLoadSequence) {
          this.rugLoading = false;
          this.setLoading(false);
        }
      }
    }

    async setProduct(product) {
      if (!product?.imageUrl || !product.variants?.length) return;
      this.productSequence += 1;
      const oldDimensions = this.placementDimensions;
      this.root.dataset.productId = String(product.id);
      this.root.dataset.productHandle = product.handle || '';
      this.root.dataset.productUrl = product.url || '';
      this.root.dataset.imageUrl = product.imageUrl;
      this.root.dataset.addToCart = 'true';
      this.shape = product.shape || 'rectangle';
      this.variantAssets = product.variantAssets || {};
      this.rugUrl = null;
      this.data = { optionNames: product.optionNames, variants: product.variants };
      this.variants = product.variants.filter((variant) => variant.available &&
        !product.excludedVariants?.map(String).includes(String(variant.id)));
      this.sizeSelect.replaceChildren(...this.variants.map((variant) => {
        const option = document.createElement('option');
        option.value = String(variant.id);
        option.textContent = variant.title;
        return option;
      }));
      const closest = this.variants.find((variant) => {
        const dimensions = window.HeikoaDimensions.parseVariantDimensions(variant, product.optionNames);
        return dimensions && oldDimensions && dimensions.widthCm === oldDimensions.widthCm &&
          dimensions.lengthCm === oldDimensions.lengthCm;
      });
      if (closest) this.sizeSelect.value = String(closest.id);
      this.root.querySelector('[data-visualizer-product-name]')?.replaceChildren(document.createTextNode(product.title));
      this.rugImage = null;
      this.rugTexture = null;
      this.rugLoading = false;
      this.updateRatioForVariant(this.sizeSelect.value, false);
      this.history = [];
      this.historyIndex = -1;
      this.open();
      this.commitHistory();
    }

    clearPhoto() {
      this.analysisSequence += 1;
      roomSession = null;
      this.photo?.close?.();
      this.photo = null;
      this.root.querySelector('[data-visualizer-comparison]')?.removeAttribute('checked');
      const comparison = this.root.querySelector('[data-visualizer-comparison]');
      if (comparison) comparison.checked = false;
      this.stage.classList.remove('is-original');
      this.automaticOcclusionMask = null;
      this.floorBoundary = null;
      this.occlusionStrokes = [];
      this.empty.hidden = false;
      this.status.textContent = '';
      this.occlusionToggle.disabled = true;
      this.setOcclusionMode(false);
      this.setLoading(false);
      this.root.querySelectorAll('[data-visualizer-file], [data-visualizer-camera]').forEach((input) => { input.value = ''; });
      this.history = [];
      this.historyIndex = -1;
      this.updateHistoryButtons();
      this.requestRender();
    }

    async adoptRoomAnalysis(session) {
      try {
        const mask = await session.analysis;
        if (roomSession !== session || this.photo !== session.photo) return;
        session.mask = mask;
        session.floorBoundary = mask.floorBoundary;
        this.automaticOcclusionMask = mask;
        this.floorBoundary = mask.floorBoundary;
        this.occlusionDirty = true;
        this.status.textContent = this.root.dataset.roomAnalysisReady;
        this.requestRender();
      } catch {
        if (roomSession === session) this.status.textContent = this.root.dataset.roomAnalysisError;
      }
    }

    prepareRugTexture(image) {
      if (this.shape !== 'round' && this.shape !== 'oval') return image;
      const texture = document.createElement('canvas');
      texture.width = image.naturalWidth;
      texture.height = image.naturalHeight;
      const context = texture.getContext('2d');
      context.beginPath();
      context.ellipse(
        texture.width / 2,
        texture.height / 2,
        texture.width / 2,
        texture.height / 2,
        0,
        0,
        Math.PI * 2,
      );
      context.clip();
      context.drawImage(image, 0, 0);
      return texture;
    }

    async readPhoto(file) {
      this.status.textContent = '';
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        this.status.textContent = this.root.dataset.photoTypeError;
        return;
      }
      if (file.size > MAX_PHOTO_BYTES) {
        this.status.textContent = this.root.dataset.photoSizeError;
        return;
      }
      this.setLoading(true);
      const analysisSequence = ++this.analysisSequence;
      try {
        let image;
        if ('createImageBitmap' in window) {
          try {
            image = await createImageBitmap(file, { imageOrientation: 'from-image' });
          } catch (bitmapError) {
            image = await this.loadLocalImage(file);
          }
        } else {
          image = await this.loadLocalImage(file);
        }
        if (analysisSequence !== this.analysisSequence) { image.close?.(); return; }
        const maxEdge = 1920;
        if (Math.max(image.width || image.naturalWidth, image.height || image.naturalHeight) > maxEdge) {
          const reduced = document.createElement('canvas');
          const scale = maxEdge / Math.max(image.width || image.naturalWidth, image.height || image.naturalHeight);
          reduced.width = Math.round((image.width || image.naturalWidth) * scale);
          reduced.height = Math.round((image.height || image.naturalHeight) * scale);
          reduced.getContext('2d').drawImage(image, 0, 0, reduced.width, reduced.height);
          image.close?.();
          image = reduced;
        }
        const previousPhoto = this.photo;
        this.photo = image;
        previousPhoto?.close?.();
        const comparison = this.root.querySelector('[data-visualizer-comparison]');
        if (comparison) comparison.checked = false;
        this.stage.classList.remove('is-original');
        this.setOcclusionMode(false);
        const session = { photo: image, mask: null, floorBoundary: null, strokes: [], analysis: null };
        roomSession = session;
        this.automaticOcclusionMask = null;
        this.floorBoundary = null;
        this.occlusionDirty = true;
        this.occlusionToggle.disabled = false;
        this.empty.hidden = true;
        this.history = [];
        this.historyIndex = -1;
        this.reset(false);
        this.commitHistory();
        this.requestRender();
        this.emit('photo_selected');
        this.setLoading(false);
        this.status.textContent = this.root.dataset.roomAnalysis;
        const placementBeforeAnalysis = this.corners
          ? this.corners.map(function (point) {
              return { x: point.x, y: point.y };
            })
          : null;
        try {
          session.analysis = this.createAutomaticOcclusionMask();
          const mask = await session.analysis;
          if (analysisSequence !== this.analysisSequence) return;
          session.mask = mask;
          session.floorBoundary = mask.floorBoundary;
          this.automaticOcclusionMask = mask;
          this.floorBoundary = mask.floorBoundary;
          this.occlusionDirty = true;
          const placementUnchanged =
            placementBeforeAnalysis &&
            this.corners &&
            placementBeforeAnalysis.every(
              (point, index) => distance(point, this.corners[index]) < 0.5,
            );
          if (placementUnchanged) this.fitRugToFloor(mask.floorBoundary);
          this.status.textContent = this.root.dataset.roomAnalysisReady;
          this.requestRender();
        } catch (analysisError) {
          if (analysisSequence !== this.analysisSequence) return;
          this.automaticOcclusionMask = null;
          this.status.textContent = this.root.dataset.roomAnalysisError;
        }
      } catch (error) {
        console.warn('Heikoa visualizer photo decoding failed:', error.name, error.message);
        if (analysisSequence === this.analysisSequence) this.status.textContent = this.root.dataset.photoError;
      } finally {
        if (analysisSequence === this.analysisSequence) this.setLoading(false);
      }
    }

    async createAutomaticOcclusionMask() {
      const photo = this.photo;
      const sequence = this.analysisSequence;
      const imageWidth = photo.width || photo.naturalWidth;
      const imageHeight = photo.height || photo.naturalHeight;
      const scale = Math.min(1, ROOM_ANALYSIS_MAX_EDGE / Math.max(imageWidth, imageHeight));
      const input = document.createElement('canvas');
      input.width = Math.max(1, Math.round(imageWidth * scale));
      input.height = Math.max(1, Math.round(imageHeight * scale));
      const context = input.getContext('2d', {willReadFrequently: true});
      context.drawImage(photo, 0, 0, input.width, input.height);
      const result = await analyzeRoom(this.root.dataset.analysisWorker,
        context.getImageData(0, 0, input.width, input.height), (update) => {
          if (sequence !== this.analysisSequence || this.photo !== photo) return;
          const label = this.root.dataset.roomAnalysis;
          this.status.textContent = update.phase ? label : label + ' ' + update.progress + ' %';
        });
      if (sequence !== this.analysisSequence || this.photo !== photo) throw new Error('room-analysis-replaced');
      const mask = document.createElement('canvas');
      mask.width = result.width;
      mask.height = result.height;
      const maskContext = mask.getContext('2d');
      const image = maskContext.createImageData(mask.width, mask.height);
      for (let p = 0; p < result.alpha.length; p++) {
        image.data[p * 4] = image.data[p * 4 + 1] = image.data[p * 4 + 2] = 255;
        image.data[p * 4 + 3] = result.alpha[p];
      }
      maskContext.putImageData(image, 0, 0);
      mask.floorBoundary = result.floorBoundary;
      return mask;
    }

    loadLocalImage(file) {
      return new Promise(function (resolve, reject) {
        const url = URL.createObjectURL(file);
        const image = new Image();
        image.onload = function () {
          URL.revokeObjectURL(url);
          resolve(image);
        };
        image.onerror = function () {
          URL.revokeObjectURL(url);
          reject(new Error('photo-load-failed'));
        };
        image.src = url;
      });
    }

    resize() {
      if (this.dialog.hidden) return;
      const rect = this.stage.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      if (width === this.viewport.width && height === this.viewport.height) return;
      const previous = this.viewport;
      const oldLayout = this.photoLayout(previous);
      this.viewport = { width, height };
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      this.canvas.width = Math.round(width * pixelRatio);
      this.canvas.height = Math.round(height * pixelRatio);
      this.pixelRatio = pixelRatio;
      this.occlusionMaskCanvas.width = this.canvas.width;
      this.occlusionMaskCanvas.height = this.canvas.height;
      this.occlusionForegroundCanvas.width = this.canvas.width;
      this.occlusionForegroundCanvas.height = this.canvas.height;
      this.occlusionDirty = true;
      if (this.corners && previous.width && previous.height) {
        const nextLayout = this.photoLayout();
        const mapPoint = function (point) {
          if (oldLayout && nextLayout) return {
            x: nextLayout.x + ((point.x - oldLayout.x) / oldLayout.width) * nextLayout.width,
            y: nextLayout.y + ((point.y - oldLayout.y) / oldLayout.height) * nextLayout.height,
          };
          return {
            x: (point.x / previous.width) * width,
            y: (point.y / previous.height) * height,
          };
        };
        this.corners = this.corners.map(mapPoint);
        this.history.forEach((state) => { if (state.corners) state.corners = state.corners.map(mapPoint); });
        this.pointers.clear();
        this.drag = null;
        this.gesture = null;
      }
      this.requestRender();
    }

    currentVariant() {
      return this.variants.find((variant) => String(variant.id) === String(this.sizeSelect.value));
    }

    currentRatio() {
      const variant = this.currentVariant();
      const dimensions = window.HeikoaDimensions.parseVariantDimensions(
        variant,
        this.data.optionNames,
      );
      return dimensions ? window.HeikoaDimensions.ratioForShape(dimensions, this.shape) : this.textureRatio();
    }

    textureRatio() {
      if (this.shape === 'round') return 1;
      const width = this.rugImage?.naturalWidth || this.rugTexture?.width;
      const height = this.rugImage?.naturalHeight || this.rugTexture?.height;
      return width && height ? Math.max(width, height) / Math.min(width, height) : 1;
    }

    setLoading(loading) {
      const indicator = this.root.querySelector('[data-visualizer-loading]');
      indicator.hidden = !loading;
      this.stage.setAttribute('aria-busy', String(loading));
    }

    syncControlValues() {
      this.root.querySelectorAll('[data-visualizer-control]').forEach((control) => {
        control.value = String(Math.round(this[control.dataset.visualizerControl] * 100));
      });
    }

    snapshot() {
      return {
        corners: this.corners
          ? this.corners.map(function (point) {
              return { x: point.x, y: point.y };
            })
          : null,
        photoZoom: this.photoZoom,
        rugOpacity: this.rugOpacity,
        rugBrightness: this.rugBrightness,
        shadowStrength: this.shadowStrength,
        occlusionStrokes: this.occlusionStrokes.map(function (stroke) {
          return {
            action: stroke.action,
            radius: stroke.radius,
            points: stroke.points.map(function (point) {
              return { x: point.x, y: point.y };
            }),
          };
        }),
        variantId: String(this.sizeSelect.value),
      };
    }

    commitHistory() {
      if (!this.corners) return;
      const state = this.snapshot();
      const previous = this.history[this.historyIndex];
      if (previous && JSON.stringify(previous) === JSON.stringify(state)) return;
      this.history = this.history.slice(0, this.historyIndex + 1);
      this.history.push(state);
      if (this.history.length > 30) this.history.shift();
      this.historyIndex = this.history.length - 1;
      this.updateHistoryButtons();
    }

    restoreHistoryState(state) {
      if (!state) return;
      this.corners = state.corners.map(function (point) {
        return { x: point.x, y: point.y };
      });
      this.photoZoom = state.photoZoom;
      this.rugOpacity = state.rugOpacity;
      this.rugBrightness = state.rugBrightness;
      this.shadowStrength = state.shadowStrength;
      this.occlusionStrokes = (state.occlusionStrokes || []).map(function (stroke) {
        return {
          action: stroke.action,
          radius: stroke.radius,
          points: stroke.points.map(function (point) {
            return { x: point.x, y: point.y };
          }),
        };
      });
      this.occlusionDirty = true;
      this.sizeSelect.value = state.variantId;
      this.loadRug();
      this.placementDimensions = window.HeikoaDimensions.parseVariantDimensions(this.currentVariant(), this.data.optionNames);
      this.syncControlValues();
      this.updateHistoryButtons();
      this.requestRender();
    }

    updateHistoryButtons() {
      this.root.querySelector('[data-visualizer-undo]').disabled = this.historyIndex <= 0;
      this.root.querySelector('[data-visualizer-redo]').disabled =
        this.historyIndex < 0 || this.historyIndex >= this.history.length - 1;
    }

    undo() {
      if (this.historyIndex <= 0) return;
      this.historyIndex -= 1;
      this.restoreHistoryState(this.history[this.historyIndex]);
    }

    redo() {
      if (this.historyIndex >= this.history.length - 1) return;
      this.historyIndex += 1;
      this.restoreHistoryState(this.history[this.historyIndex]);
    }

    reset(commit) {
      if (!this.viewport.width || !this.viewport.height) return;
      this.photoZoom = 1;
      this.rugOpacity = 1;
      this.rugBrightness = 1;
      this.shadowStrength = 0.65;
      this.occlusionStrokes = [];
      this.occlusionDirty = true;
      const ratio = Math.max(0.7, this.currentRatio());
      const layout = this.photoLayout();
      const availableWidth = layout ? Math.min(layout.width, this.viewport.width) : this.viewport.width;
      const availableHeight = layout ? Math.min(layout.height, this.viewport.height) : this.viewport.height;
      const width = Math.min(availableWidth * 0.65, 560);
      const height = Math.min(
        (width / ratio) * FLOOR_FORESHORTENING,
        availableHeight * 0.30,
      );
      const centerX = this.viewport.width / 2;
      const centerY = layout ? layout.y + layout.height * 0.78 : this.viewport.height * 0.72;
      const topWidth = width * 0.72;
      this.corners = [
        { x: centerX - topWidth / 2, y: centerY - height / 2 },
        { x: centerX + topWidth / 2, y: centerY - height / 2 },
        { x: centerX + width / 2, y: centerY + height / 2 },
        { x: centerX - width / 2, y: centerY + height / 2 },
      ];
      this.placementDimensions = window.HeikoaDimensions.parseVariantDimensions(this.currentVariant(), this.data.optionNames);
      this.syncControlValues();
      if (commit !== false) this.commitHistory();
      this.requestRender();
    }

    fitRugToFloor(normalizedBoundary) {
      if (!this.corners || !Number.isFinite(normalizedBoundary)) return;
      const layout = this.photoLayout();
      if (!layout) return;
      const floorY = layout.y + normalizedBoundary * layout.height + 6;
      const topY = (this.corners[0].y + this.corners[1].y) / 2;
      if (topY >= floorY) return;
      const bottomLimit = Math.min(this.viewport.height, layout.y + layout.height) - 12;
      const maximumY = Math.max.apply(
        null,
        this.corners.map(function (point) {
          return point.y;
        }),
      );
      const shift = Math.max(0, Math.min(floorY - topY, bottomLimit - maximumY));
      if (shift) {
        this.corners = this.corners.map(function (point) {
          return { x: point.x, y: point.y + shift };
        });
      }
      const shiftedTopY = topY + shift;
      if (shiftedTopY < floorY) {
        const bottomY = (this.corners[2].y + this.corners[3].y) / 2;
        const currentHeight = Math.max(1, bottomY - shiftedTopY);
        const scaleY = Math.max(0.35, Math.min(1, (bottomY - floorY) / currentHeight));
        this.corners = this.corners.map(function (point) {
          return { x: point.x, y: bottomY - (bottomY - point.y) * scaleY };
        });
      }
      this.commitHistory();
      this.requestRender();
    }

    updateRatioForVariant(variantId, commit) {
      const variant = this.variants.find((item) => String(item.id) === String(variantId));
      const dimensions = window.HeikoaDimensions.parseVariantDimensions(
        variant,
        this.data.optionNames,
      );
      const ratio = dimensions ? window.HeikoaDimensions.ratioForShape(dimensions, this.shape) : this.textureRatio();
      if (!this.corners || !ratio) return;
      if (dimensions && this.placementDimensions) {
        const old = this.placementDimensions;
        const scaleX = Math.max(dimensions.widthCm, dimensions.lengthCm) / Math.max(old.widthCm, old.lengthCm);
        const scaleY = Math.min(dimensions.widthCm, dimensions.lengthCm) / Math.min(old.widthCm, old.lengthCm);
        const center = centerOf(this.corners);
        const angle = Math.atan2((this.corners[1].y - this.corners[0].y) / FLOOR_FORESHORTENING,
          this.corners[1].x - this.corners[0].x);
        const cosine = Math.cos(angle);
        const sine = Math.sin(angle);
        this.corners = this.corners.map((point) => {
          const x = point.x - center.x;
          const y = (point.y - center.y) / FLOOR_FORESHORTENING;
          const along = (x * cosine + y * sine) * scaleX;
          const across = (-x * sine + y * cosine) * scaleY;
          return { x: center.x + along * cosine - across * sine,
            y: center.y + (along * sine + across * cosine) * FLOOR_FORESHORTENING };
        });
        this.placementDimensions = dimensions;
        if (commit !== false) this.commitHistory();
        this.requestRender();
        return;
      }
      const topWidth = distance(this.corners[0], this.corners[1]);
      const bottomWidth = distance(this.corners[3], this.corners[2]);
      const leftHeight = distance(this.corners[0], this.corners[3]);
      const rightHeight = distance(this.corners[1], this.corners[2]);
      const currentWidth = (topWidth + bottomWidth) / 2;
      const currentHeight = Math.max(1, (leftHeight + rightHeight) / 2);
      const scaleY = Math.max(
        0.35,
        Math.min(2.8, ((currentWidth / ratio) * FLOOR_FORESHORTENING) / currentHeight),
      );
      const center = centerOf(this.corners);
      const angle = Math.atan2(
        this.corners[1].y - this.corners[0].y,
        this.corners[1].x - this.corners[0].x,
      );
      const vertical = { x: -Math.sin(angle), y: Math.cos(angle) };
      this.corners = this.corners.map(function (point) {
        const relative = { x: point.x - center.x, y: point.y - center.y };
        const verticalDistance = relative.x * vertical.x + relative.y * vertical.y;
        return {
          x: point.x + vertical.x * verticalDistance * (scaleY - 1),
          y: point.y + vertical.y * verticalDistance * (scaleY - 1),
        };
      });
      this.placementDimensions = dimensions;
      if (commit !== false) this.commitHistory();
      this.requestRender();
    }

    rotate(degrees, commit) {
      if (!this.corners) return;
      const center = centerOf(this.corners);
      const radians = (degrees * Math.PI) / 180;
      const cosine = Math.cos(radians);
      const sine = Math.sin(radians);
      this.corners = this.corners.map(function (point) {
        const x = point.x - center.x;
        const y = point.y - center.y;
        return {
          x: center.x + x * cosine - (y / FLOOR_FORESHORTENING) * sine,
          y: center.y + x * sine * FLOOR_FORESHORTENING + y * cosine,
        };
      });
      if (commit !== false) this.commitHistory();
      this.requestRender();
    }

    scale(factor, commit) {
      if (!this.corners) return;
      const center = centerOf(this.corners);
      this.corners = this.corners.map(function (point) {
        return {
          x: center.x + (point.x - center.x) * factor,
          y: center.y + (point.y - center.y) * factor,
        };
      });
      if (commit !== false) this.commitHistory();
      this.requestRender();
    }

    eventPoint(event) {
      const rect = this.canvas.getBoundingClientRect();
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    }

    photoLayout(viewport = this.viewport) {
      if (!this.photo) return null;
      const imageWidth = this.photo.width || this.photo.naturalWidth;
      const imageHeight = this.photo.height || this.photo.naturalHeight;
      const scale =
        Math.min(viewport.width / imageWidth, viewport.height / imageHeight) * this.photoZoom;
      const width = imageWidth * scale;
      const height = imageHeight * scale;
      return {
        x: (viewport.width - width) / 2,
        y: (viewport.height - height) / 2,
        width,
        height,
      };
    }

    stagePointToPhoto(point) {
      const layout = this.photoLayout();
      if (!layout) return null;
      return {
        x: (point.x - layout.x) / layout.width,
        y: (point.y - layout.y) / layout.height,
      };
    }

    photoPointToStage(point, layout) {
      return {
        x: layout.x + point.x * layout.width,
        y: layout.y + point.y * layout.height,
      };
    }

    setOcclusionMode(active) {
      if (active && !this.photo) return;
      this.occlusionMode = Boolean(active);
      this.brushPoint = null;
      this.pointers.clear();
      this.drag = null;
      this.gesture = null;
      this.occlusionToggle.setAttribute('aria-pressed', String(this.occlusionMode));
      this.occlusionTools.hidden = !this.occlusionMode;
      this.stage.classList.toggle('is-occlusion-editing', this.occlusionMode);
      this.requestRender();
      if (this.occlusionMode) this.setOcclusionAction('hide');
    }

    setOcclusionAction(action) {
      this.occlusionAction = action === 'restore' ? 'restore' : 'hide';
      this.root.querySelectorAll('[data-visualizer-occlusion-action]').forEach((button) => {
        const selected = button.dataset.visualizerOcclusionAction === this.occlusionAction;
        button.setAttribute('aria-pressed', String(selected));
      });
      this.requestRender();
    }

    nearestHandle(point) {
      if (!this.corners) return -1;
      let match = -1;
      let nearest = HANDLE_RADIUS;
      this.corners.forEach(function (corner, index) {
        const candidate = distance(point, corner);
        if (candidate < nearest) {
          nearest = candidate;
          match = index;
        }
      });
      return match;
    }

    pointerDown(event) {
      if (!this.photo || !this.corners) return;
      event.preventDefault();
      this.interactionChanged = false;
      this.canvas.setPointerCapture(event.pointerId);
      const point = this.eventPoint(event);
      this.brushPoint = point;
      if (this.occlusionMode) {
        if (this.pointers.size) return;
        const normalizedPoint = this.stagePointToPhoto(point);
        const layout = this.photoLayout();
        if (!normalizedPoint || !layout) return;
        const stroke = {
          action: this.occlusionAction,
          radius: this.brushRadius / Math.min(layout.width, layout.height),
          points: [normalizedPoint],
        };
        this.occlusionStrokes.push(stroke);
        this.occlusionDirty = true;
        this.pointers.set(event.pointerId, point);
        this.drag = { type: 'occlusion', stroke, last: point };
        this.interactionChanged = true;
        this.requestRender();
        return;
      }
      this.pointers.set(event.pointerId, point);
      if (this.pointers.size === 2) {
        this.beginGesture();
        return;
      }
      const handle = this.nearestHandle(point);
      if (handle >= 0) this.drag = { type: 'corner', handle, last: point };
      else this.drag = { type: 'move', last: point };
    }

    beginGesture() {
      const entries = Array.from(this.pointers.entries()).slice(0, 2);
      this.gesture = {
        ids: entries.map(function (entry) {
          return entry[0];
        }),
        points: entries.map(function (entry) {
          return { x: entry[1].x, y: entry[1].y };
        }),
        corners: this.corners.map(function (point) {
          return { x: point.x, y: point.y };
        }),
      };
      this.drag = null;
    }

    pointerMove(event) {
      const hoverPoint = this.eventPoint(event);
      if (!this.occlusionMode && this.corners) {
        this.canvas.style.cursor = this.pointers.size ? 'grabbing' :
          this.nearestHandle(hoverPoint) >= 0 ? 'nwse-resize' :
          'grab';
      }
      if (this.occlusionMode) {
        this.brushPoint = hoverPoint;
        this.requestRender();
      }
      if (!this.pointers.has(event.pointerId)) return;
      event.preventDefault();
      const point = hoverPoint;
      this.pointers.set(event.pointerId, point);
      if (this.drag && this.drag.type === 'occlusion') {
        if (distance(point, this.drag.last) < 2) return;
        const normalizedPoint = this.stagePointToPhoto(point);
        if (!normalizedPoint) return;
        this.drag.stroke.points.push(normalizedPoint);
        this.occlusionDirty = true;
        this.drag.last = point;
        this.interactionChanged = true;
        this.requestRender();
        return;
      }
      if (this.gesture && this.pointers.size >= 2) {
        const current = this.gesture.ids.map((id) => this.pointers.get(id));
        if (current.some((item) => !item)) return;
        const baseCenter = centerOf(this.gesture.points);
        const currentCenter = centerOf(current);
        const groundDistance = (a, b) => Math.hypot(a.x - b.x, (a.y - b.y) / FLOOR_FORESHORTENING);
        const baseDistance = Math.max(1, groundDistance(this.gesture.points[0], this.gesture.points[1]));
        const scale = Math.max(0.15, Math.min(6, groundDistance(current[0], current[1]) / baseDistance));
        const baseAngle = Math.atan2(
          (this.gesture.points[1].y - this.gesture.points[0].y) / FLOOR_FORESHORTENING,
          this.gesture.points[1].x - this.gesture.points[0].x,
        );
        const currentAngle = Math.atan2((current[1].y - current[0].y) / FLOOR_FORESHORTENING, current[1].x - current[0].x);
        const angle = currentAngle - baseAngle;
        const cosine = Math.cos(angle);
        const sine = Math.sin(angle);
        this.corners = this.gesture.corners.map(function (corner) {
          const x = (corner.x - baseCenter.x) * scale;
          const y = ((corner.y - baseCenter.y) / FLOOR_FORESHORTENING) * scale;
          return {
            x: currentCenter.x + x * cosine - y * sine,
            y: currentCenter.y + (x * sine + y * cosine) * FLOOR_FORESHORTENING,
          };
        });
        this.interactionChanged = true;
        this.requestRender();
        return;
      }
      if (!this.drag) return;
      if (this.drag.type === 'corner') {
        const candidate = this.corners.map((point) => ({ ...point }));
        candidate[this.drag.handle] = {
          x: point.x,
          y: point.y,
        };
        if (validQuad(candidate)) this.corners = candidate;
      } else {
        const delta = { x: point.x - this.drag.last.x, y: point.y - this.drag.last.y };
        this.corners = this.corners.map(function (corner) {
          return { x: corner.x + delta.x, y: corner.y + delta.y };
        });
      }
      this.drag.last = point;
      this.interactionChanged = true;
      this.requestRender();
    }

    pointerUp(event) {
      this.pointers.delete(event.pointerId);
      if (this.pointers.size < 2) this.gesture = null;
      this.drag = this.pointers.size === 1 && !this.occlusionMode
        ? {type: 'move', last: Array.from(this.pointers.values())[0]}
        : null;
      if (this.interactionChanged && this.pointers.size === 0) {
        this.commitHistory();
        this.interactionChanged = false;
      }
    }

    requestRender() {
      if (this.frameRequested) return;
      this.frameRequested = true;
      requestAnimationFrame(() => {
        this.frameRequested = false;
        this.render();
      });
    }

    render() {
      const context = this.context;
      const width = this.viewport.width;
      const height = this.viewport.height;
      if (!width || !height) return;
      context.setTransform(this.pixelRatio, 0, 0, this.pixelRatio, 0, 0);
      context.clearRect(0, 0, width, height);
      context.fillStyle = '#edf0eb';
      context.fillRect(0, 0, width, height);
      if (this.photo) this.drawPhoto(context, width, height);
      else {
        const gradient = context.createLinearGradient(0, 0, width, height);
        gradient.addColorStop(0, '#eef0ec');
        gradient.addColorStop(1, '#cbd2ce');
        context.fillStyle = gradient;
        context.fillRect(0, 0, width, height);
      }
      if (!this.photo || !this.rugTexture || !this.corners) return;
      if (this.stage.classList.contains('is-original')) return;
      const layout = this.photoLayout();
      context.save();
      context.beginPath();
      context.rect(layout.x, layout.y, layout.width, layout.height);
      context.clip();
      this.drawRug(context);
      this.drawOcclusionForeground(context, width, height);
      if (this.occlusionMode) this.drawBrushIndicator(context);
      context.restore();
    }

    drawPhoto(context, width, height) {
      const layout = this.photoLayout();
      if (!layout) return;
      context.drawImage(this.photo, layout.x, layout.y, layout.width, layout.height);
    }

    drawRug(context) {
      const homography = homographyFor(this.corners);
      if (!homography) return;
      // Project the actual alpha silhouette so irregular rugs have no box shadow.
      const shadow = this.rugShadowCanvas;
      shadow.width = Math.ceil(this.viewport.width);
      shadow.height = Math.ceil(this.viewport.height);
      const shadowContext = shadow.getContext('2d');
      this.drawRugMesh(shadowContext, homography);
      shadowContext.globalCompositeOperation = 'source-in';
      shadowContext.fillStyle = '#000';
      shadowContext.fillRect(0, 0, shadow.width, shadow.height);
      shadowContext.globalCompositeOperation = 'source-over';
      context.save();
      context.filter = 'blur(2px)';
      context.globalAlpha = 0.12 * this.shadowStrength * this.rugOpacity;
      context.drawImage(shadow, 0, 2);
      context.restore();
      context.save();
      context.globalAlpha = this.rugOpacity;
      context.filter = `brightness(${this.rugBrightness}) saturate(0.94) contrast(0.97)`;
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      this.drawRugMesh(context, homography);
      context.restore();
    }

    drawRugMesh(context, homography) {
      const imageWidth = this.rugTexture.width || this.rugTexture.naturalWidth;
      const imageHeight = this.rugTexture.height || this.rugTexture.naturalHeight;
      for (let row = 0; row < MESH_SIZE; row += 1) {
        for (let column = 0; column < MESH_SIZE; column += 1) {
          const u0 = column / MESH_SIZE;
          const u1 = (column + 1) / MESH_SIZE;
          const v0 = row / MESH_SIZE;
          const v1 = (row + 1) / MESH_SIZE;
          const sourceTopLeft = { x: u0 * imageWidth, y: v0 * imageHeight };
          const sourceTopRight = { x: u1 * imageWidth, y: v0 * imageHeight };
          const sourceBottomRight = { x: u1 * imageWidth, y: v1 * imageHeight };
          const sourceBottomLeft = { x: u0 * imageWidth, y: v1 * imageHeight };
          const topLeft = projectPoint(homography, u0, v0);
          const topRight = projectPoint(homography, u1, v0);
          const bottomRight = projectPoint(homography, u1, v1);
          const bottomLeft = projectPoint(homography, u0, v1);
          drawTriangle(
            context,
            this.rugTexture,
            [sourceTopLeft, sourceTopRight, sourceBottomRight],
            [topLeft, topRight, bottomRight],
          );
          drawTriangle(
            context,
            this.rugTexture,
            [sourceTopLeft, sourceBottomRight, sourceBottomLeft],
            [topLeft, bottomRight, bottomLeft],
          );
        }
      }
    }

    drawOcclusionForeground(context, width, height) {
      const automaticMask = this.automaticOcclusionEnabled ? this.automaticOcclusionMask : null;
      if (!automaticMask && !this.occlusionStrokes.length) return;
      const layout = this.photoLayout();
      if (!layout) return;
      const foreground = this.occlusionForegroundCanvas;
      if (this.occlusionDirty) {
        const mask = this.occlusionMaskCanvas;
        const maskContext = mask.getContext('2d');
        maskContext.setTransform(1, 0, 0, 1, 0, 0);
        maskContext.clearRect(0, 0, mask.width, mask.height);
        maskContext.setTransform(this.pixelRatio, 0, 0, this.pixelRatio, 0, 0);
        if (automaticMask) {
          maskContext.imageSmoothingEnabled = true;
          maskContext.imageSmoothingQuality = 'high';
          maskContext.drawImage(automaticMask, layout.x, layout.y, layout.width, layout.height);
        }
        maskContext.lineCap = 'round';
        maskContext.lineJoin = 'round';
        this.occlusionStrokes.forEach((stroke) => {
          const points = stroke.points.map((point) => this.photoPointToStage(point, layout));
          if (!points.length) return;
          maskContext.globalCompositeOperation =
            stroke.action === 'restore' ? 'destination-out' : 'source-over';
          maskContext.strokeStyle = '#000';
          maskContext.fillStyle = '#000';
          maskContext.lineWidth = stroke.radius * Math.min(layout.width, layout.height) * 2;
          if (points.length === 1) {
            maskContext.beginPath();
            maskContext.arc(points[0].x, points[0].y, maskContext.lineWidth / 2, 0, Math.PI * 2);
            maskContext.fill();
            return;
          }
          maskContext.beginPath();
          maskContext.moveTo(points[0].x, points[0].y);
          points.slice(1).forEach(function (point) {
            maskContext.lineTo(point.x, point.y);
          });
          maskContext.stroke();
        });
        maskContext.globalCompositeOperation = 'source-over';

        const foregroundContext = foreground.getContext('2d');
        foregroundContext.setTransform(1, 0, 0, 1, 0, 0);
        foregroundContext.clearRect(0, 0, foreground.width, foreground.height);
        foregroundContext.setTransform(this.pixelRatio, 0, 0, this.pixelRatio, 0, 0);
        this.drawPhoto(foregroundContext, width, height);
        foregroundContext.globalCompositeOperation = 'destination-in';
        foregroundContext.setTransform(1, 0, 0, 1, 0, 0);
        foregroundContext.drawImage(mask, 0, 0);
        foregroundContext.globalCompositeOperation = 'source-over';
        this.occlusionDirty = false;
      }

      context.save();
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.drawImage(foreground, 0, 0);
      context.restore();
    }

    drawBrushIndicator(context) {
      if (!this.brushPoint) return;
      context.save();
      context.beginPath();
      context.arc(this.brushPoint.x, this.brushPoint.y, this.brushRadius, 0, Math.PI * 2);
      context.fillStyle = this.occlusionAction === 'restore' ? 'rgba(255, 255, 255, 0.18)' : 'rgba(31, 100, 85, 0.2)';
      context.fill();
      context.strokeStyle = this.occlusionAction === 'restore' ? '#ffffff' : '#1f6455';
      context.lineWidth = 2;
      context.stroke();
      context.restore();
    }

    selectVariant(variantId, reshape) {
      const option = Array.from(this.sizeSelect.options).find(function (item) {
        return String(item.value) === String(variantId);
      });
      if (!option) return;
      this.sizeSelect.value = String(variantId);
      this.selectedVariantId = String(variantId);
      if (reshape !== false) this.updateRatioForVariant(variantId);
      this.loadRug();
    }

    emit(eventName) {
      this.root.dispatchEvent(
        new CustomEvent('heikoa:visualizer', {
          bubbles: true,
          detail: {
            event: eventName,
            productId: this.root.dataset.productId || '',
            variantId: String(this.sizeSelect.value || ''),
            shape: this.shape,
            context: this.root.dataset.productUrl ? 'dedicated_page' : 'product_page',
          },
        }),
      );
    }

    confirm() {
      const variant = this.currentVariant();
      if (!variant) return;
      this.emit('confirmed');
      if (this.root.dataset.addToCart === 'true') {
        const form = this.root.querySelector('[data-visualizer-cart-form]');
        const variantInput = this.root.querySelector('[data-visualizer-cart-variant]');
        if (!form || !variantInput) return;
        this.selectedVariantId = String(variant.id);
        variantInput.value = String(variant.id);
        this.status.textContent = this.root.dataset.cartSubmitting;
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.submit();
        return;
      }
      if (this.root.dataset.productUrl) {
        this.selectedVariantId = String(variant.id);
        this.status.textContent = this.root.dataset.selectionConfirmed;
        window.setTimeout(() => {
          window.location.assign(`${this.root.dataset.productUrl}?variant=${encodeURIComponent(variant.id)}`);
        }, 420);
        return;
      }
      const section = document.getElementById(`shopify-section-${this.sectionId}`) || document;
      const radios = section.querySelector(`variant-radios[data-section="${this.sectionId}"]`);
      const selects = section.querySelector(`variant-selects[data-section="${this.sectionId}"]`);
      if (radios) {
        const fieldsets = radios.querySelectorAll('fieldset');
        variant.options.forEach(function (value, index) {
          const input = Array.from(fieldsets[index]?.querySelectorAll('input[type="radio"]') || []).find(
            function (candidate) {
              return candidate.value === value;
            },
          );
          if (input) input.checked = true;
        });
        radios.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (selects) {
        const controls = selects.querySelectorAll('select');
        variant.options.forEach(function (value, index) {
          if (controls[index]) controls[index].value = value;
        });
        selects.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        const input = section.querySelector(`#product-form-${this.sectionId} input[name="id"]`);
        if (input) {
          input.value = String(variant.id);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      this.selectedVariantId = String(variant.id);
      this.status.textContent = this.root.dataset.selectionConfirmed;
      window.setTimeout(() => this.close(), 420);
    }
  }

  function initialize(root) {
    if (root.dataset.visualizerReady === 'true') return;
    root.dataset.visualizerReady = 'true';
    let instance;
    root.addEventListener('click', (event) => {
      if (!event.target.closest('[data-visualizer-open]') || instance) return;
      instance = new RugVisualizer(root);
      instance.open();
    });
    root.addEventListener('heikoa:activate', () => {
      if (!instance) instance = new RugVisualizer(root);
    });
  }

  function initializeAll(scope) {
    scope.querySelectorAll('[data-heikoa-visualizer]').forEach(initialize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(document));
  } else {
    initializeAll(document);
  }
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
